// A bottom sheet's three heights, for the panels that live under the
// timeline and the pads on a phone — the material shelf and the clip editor.
//
// Not an overlay: the sheet is IN the column, so it pushes the view up rather
// than covering it, and the transport at the bottom is never under it. Three
// snaps — peek (a one-line strip), half, full — reached by the header, which
// is the handle: tap to open or shut, drag up or down for the next size.
//
// Two sheets share one column; the page keeps them from both being open by
// sending the other to its peek (`SheetSnap`'s `yield`).

import { useCallback, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";

export type SheetSnap = "peek" | "half" | "full";

/** The drag that changes a snap: less than this is a tap. */
const SNAP_DRAG_PX = 40;

export interface SheetHandle {
  onPointerDown: (e: ReactPointerEvent) => void;
  onPointerUp: (e: ReactPointerEvent) => void;
  onPointerCancel: () => void;
}

export interface SheetSnapApi {
  snap: SheetSnap;
  open: boolean;
  setSnap: (s: SheetSnap) => void;
  /** Tap on the header: peek ↔ half. */
  toggle: () => void;
  /** Open at `to` (half unless said) if shut; leave an open sheet as it is. */
  show: (to?: SheetSnap) => void;
  hide: () => void;
  /** The header's pointer handlers: a drag of 40 px up or down steps the snap. */
  handle: SheetHandle;
  /** Height classes for the sheet's box at this snap (nothing at peek — the
   * header's own height is the peek). */
  heightClass: string;
}

/** Tailwind classes per snap; `dvh` so the browser chrome does not eat them. */
const HEIGHTS: Record<SheetSnap, string> = { peek: "", half: "h-[46dvh]", full: "h-[82dvh]" };

export function useSheetSnap(initial: SheetSnap = "peek", onChange?: (next: SheetSnap) => void): SheetSnapApi {
  const [snap, setSnapState] = useState<SheetSnap>(initial);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const setSnap = useCallback((s: SheetSnap) => {
    setSnapState((prev) => {
      if (prev !== s) onChangeRef.current?.(s);
      return s;
    });
  }, []);
  const toggle = useCallback(() => {
    setSnapState((prev) => {
      const next: SheetSnap = prev === "peek" ? "half" : "peek";
      onChangeRef.current?.(next);
      return next;
    });
  }, []);
  const show = useCallback(
    (to: SheetSnap = "half") => {
      setSnapState((prev) => {
        if (prev !== "peek") return prev;
        onChangeRef.current?.(to);
        return to;
      });
    },
    [],
  );
  const hide = useCallback(() => setSnap("peek"), [setSnap]);
  const downY = useRef<number | null>(null);
  const handle: SheetHandle = {
    onPointerDown: (e) => {
      downY.current = e.clientY;
    },
    onPointerUp: (e) => {
      const from = downY.current;
      downY.current = null;
      if (from === null) return;
      const dy = e.clientY - from;
      if (dy < -SNAP_DRAG_PX) setSnapState((s) => step(s, "up", onChangeRef.current));
      else if (dy > SNAP_DRAG_PX) setSnapState((s) => step(s, "down", onChangeRef.current));
    },
    onPointerCancel: () => {
      downY.current = null;
    },
  };
  return { snap, open: snap !== "peek", setSnap, toggle, show, hide, handle, heightClass: HEIGHTS[snap] };
}

function step(s: SheetSnap, dir: "up" | "down", onChange?: (next: SheetSnap) => void): SheetSnap {
  const order: SheetSnap[] = ["peek", "half", "full"];
  const i = order.indexOf(s);
  const next = order[Math.max(0, Math.min(order.length - 1, i + (dir === "up" ? 1 : -1)))] ?? s;
  if (next !== s) onChange?.(next);
  return next;
}
